import type { DateIso } from '../../types/domain';
import { addDays, dateFromIso, getHolidaysCached, isoStr, isWE } from '../dates';

// Jour de la semaine ISO : 1 = lundi … 7 = dimanche.
export const isoWeekday = (d: Date): number => (d.getDay() === 0 ? 7 : d.getDay());

// Jour ouvré : ni week-end ni férié.
export const isOpenDay = (d: Date): boolean =>
  !isWE(d) && !getHolidaysCached(d.getFullYear()).has(isoStr(d));

// Le membre travaille ce jour de la semaine (aucun jour fixe = tous les jours).
export const worksOn = (devWorkDays: number[], d: Date): boolean =>
  devWorkDays.length === 0 || devWorkDays.includes(isoWeekday(d));

// Dernier jour ouvré et travaillé par le membre dans la plage, ou null s'il n'y en a aucun.
export function lastWorkedDay(debut: DateIso, fin: DateIso, devWorkDays: number[]): DateIso | null {
  for (let d = dateFromIso(fin); d >= dateFromIso(debut); d = addDays(d, -1))
    if (isOpenDay(d) && worksOn(devWorkDays, d)) return isoStr(d);
  return null;
}

// Jours de la semaine (ISO) ouvrés de la plage que le membre ne travaille pas.
export function nonWorkedWeekdays(
  debut: DateIso,
  fin: DateIso,
  devWorkDays: number[],
): Set<number> {
  const jours = new Set<number>();
  for (let d = dateFromIso(debut); d <= dateFromIso(fin); d = addDays(d, 1))
    if (isOpenDay(d) && !worksOn(devWorkDays, d)) jours.add(isoWeekday(d));
  return jours;
}

// Appelle fn(iso) pour chaque jour ouvré et travaillé de la plage ; s'arrête si fn renvoie une valeur.
export function findWorkedDay<T>(
  debut: DateIso,
  fin: DateIso,
  devWorkDays: number[],
  fn: (iso: DateIso) => T | undefined,
): T | undefined {
  for (let d = dateFromIso(debut); d <= dateFromIso(fin); d = addDays(d, 1)) {
    if (!isOpenDay(d) || !worksOn(devWorkDays, d)) continue;
    const r = fn(isoStr(d));
    if (r !== undefined) return r;
  }
  return undefined;
}
